import React, { useState } from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { Plus, MessageSquareWarning, Clock } from "lucide-react";
import { api, relativeTime } from "@/lib/api";
import { Skeleton, StatusPill, PriorityDot, Empty } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);

export default function BuyerTickets() {
  const { data } = useSWR("/tickets", fetcher);
  const [filter, setFilter] = useState("all");
  const filtered = data?.filter(t => {
    if (filter === "open") return ["open", "in_progress"].includes(t.status);
    if (filter === "resolved") return ["resolved", "closed"].includes(t.status);
    return true;
  });

  const counts = {
    all: data?.length || 0,
    open: data?.filter(t => ["open", "in_progress"].includes(t.status)).length || 0,
    resolved: data?.filter(t => ["resolved", "closed"].includes(t.status)).length || 0,
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="rc-title-1">My Queries</div>
          <div className="rc-callout">Raise observations, get clear timelines, see progress.</div>
        </div>
        <Link to="/buyer/tickets/new" className="rc-btn rc-btn-sm rc-btn-orange" data-testid="new-ticket-btn"><Plus size={16}/> New</Link>
      </div>

      <div className="flex gap-2">
        {[["all","All"],["open","Open"],["resolved","Resolved"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`rc-btn rc-btn-sm ${filter === k ? "rc-btn-primary" : "rc-btn-secondary"}`}
            data-testid={`tix-filter-${k}`}>
            {lbl} <span className="ml-1 opacity-70">({counts[k]})</span>
          </button>
        ))}
      </div>

      {!data && <Skeleton h={120}/>}
      {data && filtered.length === 0 && (
        <div className="rc-card rc-card-padded">
          <Empty icon={MessageSquareWarning} title="All clear!" subtitle="No open queries. We're here whenever you need." />
        </div>
      )}

      <div className="space-y-3">
        {filtered?.map((t) => (
          <Link key={t.ticket_id} to={`/buyer/tickets/${t.ticket_id}`} className="block rc-card rc-card-padded" data-testid={`tix-${t.ticket_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <PriorityDot priority={t.priority} />
                  <div className="rc-headline" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.25 }}>{t.subject}</div>
                </div>
                <div className="rc-footnote mt-1">{t.category} · #{t.ticket_id.slice(0, 8)}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <StatusPill status={t.sla_breached && !["resolved","closed"].includes(t.status) ? "breached" : t.status} />
                <div className="rc-caption mt-1 flex items-center justify-end gap-1"><Clock size={11}/>{relativeTime(t.updated_at)}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
